import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import config from "../../config";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, CalendarDays, Check, ChevronDown, Download, RefreshCw, Search } from "lucide-react";
import { DAY, ymd } from "../lib/format";
import { PageLoader } from "../components/PageLoader";
import { Amount, IconChip, Pagination, paper, txnIcon, typeChip, typeLabel, usePagination } from "../components/wallet/parts";
import { apiGet, count, dateTime, money4, shortDate, signed, toTxn } from "../components/wallet/api";

/**
 * Wallet → Transactions (design: screens/WalletHistory.tsx).
 *
 * Layout, wording and styling are the design's. Amounts are USD.
 *
 * Real data and actions:
 *   rows, date, type, amount, balance after ...... GET /api/waba/wallet/transactions
 *                                                   (page, limit, type, start_date, end_date)
 *   type counts within the date range ............ same endpoint, limit=1, its pagination.total
 *   date ranges and type filter .................. sent to the backend; kept in the URL (?type, ?range, ?from, ?to)
 *   search across every transaction ............... same endpoint, q (item 127)
 *   net, credits and debits of the filtered set ... same endpoint, totals (128)
 *   each row's channel, template, recipient,
 *   agent and call duration ....................... same endpoint, structured fields (129; see toTxn)
 *   Export CSV (old TransactionHistory) ........... GET /api/waba/wallet/transactions/export (type, start_date,
 *                                                   end_date, q: the same search as the list, so the file matches)
 * A list that fails to load says so with Try again, rather than "No transactions".
 */

const TYPES = [
  { id: "all", label: "All" },
  { id: "deduction", label: "Deductions" },
  { id: "refund", label: "Refunds" },
  { id: "recharge", label: "Recharges" },
];

const RANGES = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "month", label: "This month" },
  { id: "lastmonth", label: "Last month" },
  { id: "90d", label: "Last 3 months" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom range" },
];

/** [start_date, end_date] (inclusive, YYYY-MM-DD) for the backend; null = open. */
function rangeDates(id, from, to) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  switch (id) {
    case "today":
      return [ymd(today), ymd(today)];
    case "yesterday":
      return [ymd(today - DAY), ymd(today - DAY)];
    case "7d":
      return [ymd(today - 6 * DAY), null];
    case "30d":
      return [ymd(today - 29 * DAY), null];
    case "month":
      return [ymd(monthStart), null];
    case "lastmonth":
      return [ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime()), ymd(monthStart - DAY)];
    case "90d":
      return [ymd(today - 89 * DAY), null];
    case "custom":
      return [from || null, to || null];
    default:
      return [null, null];
  }
}

function query(params) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== null && v !== undefined && v !== "" && v !== "all") q.set(k, v);
  return q.toString();
}

const fromIso = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).getTime();
};

function RangePicker({ range, from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(from);
  const [t, setT] = useState(to);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    setF(from || ymd(Date.now() - 13 * DAY));
    setT(to || ymd(Date.now()));
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, from, to]);

  const label =
    range === "custom" && from && to
      ? `${shortDate(fromIso(from))} to ${shortDate(fromIso(to))}`
      : RANGES.find((r) => r.id === range)?.label ?? "All time";
  const bad = !!f && !!t && f > t;

  return (
    <div ref={ref} className="relative">
      <button
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-10 items-center gap-2 rounded-full pl-4 pr-3 text-small font-medium ring-1 ${range !== "all" ? "bg-ink text-paper ring-ink" : "bg-paper/70 text-ink ring-paper hover:bg-paper"}`}
      >
        <CalendarDays size={16} strokeWidth={1.75} />
        {label}
        <ChevronDown size={16} strokeWidth={1.75} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div role="dialog" aria-label="Date range" className="absolute left-0 top-12 z-30 flex w-[440px] animate-nav-in overflow-hidden rounded-lg bg-paper shadow-md ring-1 ring-line">
          <ul className="flex w-44 flex-col border-r border-line p-2">
            {RANGES.filter((r) => r.id !== "custom").map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => {
                    onChange(r.id);
                    setOpen(false);
                  }}
                  className={`flex h-9 w-full items-center justify-between rounded-md px-3 text-left text-small ${range === r.id ? "bg-mist font-semibold" : "hover:bg-mist"}`}
                >
                  {r.label}
                  {range === r.id && <Check size={14} strokeWidth={2} />}
                </button>
              </li>
            ))}
          </ul>
          <form
            className="flex flex-1 flex-col gap-3 p-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (bad || !f || !t) return;
              onChange("custom", f, t);
              setOpen(false);
            }}
          >
            <span className="text-small font-semibold">Custom range</span>
            <label className="flex flex-col gap-1 text-caption text-muted">
              From
              <input type="date" value={f} max={ymd(Date.now())} onChange={(e) => setF(e.target.value)} className="h-10 rounded-full border border-line px-4 text-small text-ink outline-none focus:border-ink" />
            </label>
            <label className="flex flex-col gap-1 text-caption text-muted">
              To
              <input type="date" value={t} max={ymd(Date.now())} onChange={(e) => setT(e.target.value)} className="h-10 rounded-full border border-line px-4 text-small text-ink outline-none focus:border-ink" />
            </label>
            {bad && <p className="text-caption font-medium text-danger">Start date is after the end date</p>}
            <button type="submit" disabled={bad || !f || !t} className="mt-auto flex h-10 items-center justify-center rounded-full bg-ink text-small font-semibold text-paper hover:bg-ink/85 disabled:opacity-40">
              Apply range
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default function WalletHistory() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "all";
  const range = params.get("range") || "all";
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const [q, setQ] = useState("");
  const [search, setSearch] = useState(""); // q, debounced, sent to the backend
  useEffect(() => { const id = setTimeout(() => setSearch(q.trim()), 350); return () => clearTimeout(id); }, [q]);
  const [page, setPage] = useState(null); // { key, rows, total } for the page on screen
  const [counts, setCounts] = useState({ all: null, deduction: null, refund: null, recharge: null });
  const [retry, setRetry] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const update = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (!v || v === "all") next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  const [start, end] = useMemo(() => rangeDates(range, from, to), [range, from, to]);

  // Date range narrows the set; the type pills count within it.
  useEffect(() => {
    let live = true;
    Promise.all(
      TYPES.map((t) => apiGet(`/api/waba/wallet/transactions?${query({ limit: 1, type: t.id, start_date: start, end_date: end, q: search || undefined })}`)),
    ).then((res) => {
      if (!live) return;
      const c = {};
      TYPES.forEach((t, i) => (c[t.id] = res[i]?.success ? Number(res[i].pagination?.total) || 0 : null));
      setCounts(c);
    });
    return () => {
      live = false;
    };
  }, [start, end, search]);

  const listRef = useRef(null);
  const pg = usePagination(page?.total ?? 0, JSON.stringify({ type, range, from, to, search }));
  // The filters and page the list on screen was loaded for. Until the answer for
  // the current ones arrives (a new type, range, search or page), show the
  // loader rather than the previous list under the new filter.
  const pageKey = JSON.stringify({ page: pg.page, size: pg.size, type, start, end, search });
  const loading = page?.key !== pageKey;

  useEffect(() => {
    let live = true;
    const key = pageKey;
    apiGet(`/api/waba/wallet/transactions?${query({ page: pg.page, limit: pg.size, type, start_date: start, end_date: end, q: search || undefined })}`).then((r) => {
      if (!live) return;
      setPage({
        key,
        rows: r?.success ? (r.transactions || []).map(toTxn) : [], total: r?.success ? Number(r.pagination?.total) || 0 : 0,
        totals: r?.success && r.totals ? r.totals : null,
        failed: !r?.success,
      });
    });
    return () => {
      live = false;
    };
  }, [pg.page, pg.size, type, start, end, search, retry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Streams the CSV for the type and dates on screen; the file name is the server's.
  const exportCsv = async () => {
    setExporting(true);
    setExportError("");
    try {
      const r = await axios.get(`${config.API_BASE_URL}/api/waba/wallet/transactions/export?${query({ type, start_date: start, end_date: end, q: search || undefined })}`, { responseType: "blob", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const name = /filename="?([^";]+)"?/i.exec(r.headers?.["content-disposition"] || "")?.[1] || `wallet-transactions-${ymd(Date.now())}.csv`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      let message = "";
      try { message = JSON.parse(await e.response?.data?.text?.())?.message || ""; } catch (_) { /* not JSON */ }
      setExportError(message || (e.response?.status === 403 ? "You don't have access to export the wallet." : "The export didn't download. Try again."));
    } finally { setExporting(false); }
  };

  // Search runs on the backend across every transaction (item 127).
  const rows = page?.rows || [];
  // The whole filtered set's net (item 128); the page's own as a fallback for an older backend.
  const net = page?.totals ? Number(page.totals.net) : rows.reduce((s, t) => s + t.amount, 0);
  const netIsAll = !!page?.totals;

  const clear = () => {
    setQ("");
    setParams(new URLSearchParams(), { replace: true });
  };

  return (
    <div className="mx-auto flex max-w-7xl animate-fade-in flex-col gap-6 px-8 py-9">
      <header className="flex flex-col gap-3">
        <Link to="/wallet" className="flex w-fit items-center gap-1.5 text-small font-medium text-muted hover:text-ink">
          <ArrowLeft size={16} strokeWidth={1.75} />
          Wallet
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h1 className="text-h1 font-semibold">Transactions</h1>
          <div className="flex flex-col items-end gap-1">
            <button type="button" onClick={exportCsv} disabled={exporting} className="flex h-10 items-center gap-2 rounded-full bg-paper px-4 text-small font-semibold ring-1 ring-line hover:bg-mist disabled:opacity-60">
              <Download size={16} strokeWidth={1.75} />
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
            {exportError && <span role="alert" className="text-caption text-danger">{exportError}</span>}
          </div>
        </div>
      </header>

      <div className="relative z-20 flex flex-wrap items-center gap-3">
        <div role="tablist" aria-label="Type" className="flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={type === t.id}
              onClick={() => update({ type: t.id })}
              className={`flex h-10 items-center gap-2 rounded-full px-4 text-small font-medium ${type === t.id ? "bg-ink text-paper" : "bg-paper/70 text-ink ring-1 ring-paper hover:bg-paper"}`}
            >
              {t.label}
              <span className={`tabular-nums ${type === t.id ? "text-paper/60" : "text-muted"}`}>{count(counts[t.id])}</span>
            </button>
          ))}
        </div>

        <RangePicker range={range} from={from} to={to} onChange={(r, f, t) => update({ range: r, from: f, to: t })} />

        <label className="flex h-10 w-full items-center sm:ml-auto sm:w-72 gap-2 rounded-full bg-paper px-4 ring-1 ring-line focus-within:ring-ink/30">
          <Search size={16} strokeWidth={1.75} className="text-muted" />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
            }}
            aria-label="Search number, template or agent"
            placeholder="Search number, template or agent"
            className="h-full flex-1 bg-transparent text-small outline-none placeholder:text-muted"
          />
        </label>
      </div>

      {loading ? (
        <PageLoader label="Loading transactions…" />
      ) : page.failed ? (
        <div role="alert" className={`${paper} flex flex-col items-center gap-3 px-6 py-14 text-center`}>
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-danger-soft text-danger"><AlertCircle size={22} strokeWidth={1.75} /></span>
          <p className="text-body font-semibold">Couldn't load the transactions</p>
          <button type="button" onClick={() => { setPage(null); setRetry((n) => n + 1); }} className="flex h-9 items-center gap-1.5 rounded-full px-4 text-caption font-semibold ring-1 ring-line hover:bg-mist"><RefreshCw size={14} strokeWidth={1.75} />Try again</button>
        </div>
      ) : (
        <div ref={listRef}>
          <section aria-label="Transactions" className={`${paper} animate-fade-in overflow-hidden`}>
            <div className="grid grid-cols-[150px_130px_1fr_130px_130px] items-center gap-4 border-b border-line px-6 py-3 text-caption font-medium text-muted">
              <span>Date</span>
              <span>Type</span>
              <span>Details</span>
              <span className="text-right">Amount</span>
              <span className="text-right">Balance</span>
            </div>
            {rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
                <p className="text-body font-semibold">{search ? `No transactions match "${search}"` : "No transactions in this period"}</p>
                <p className="text-small text-muted">Try another date range or clear filters</p>
                <button onClick={clear} className="mt-2 flex h-10 items-center rounded-full px-5 text-small font-semibold ring-1 ring-line hover:bg-mist">Clear filters</button>
              </div>
            ) : (
              <ul key={`${type}-${range}-${from}-${to}-${pg.page}`}>
                {rows.map((t, i) => (
                  <li key={t.id} style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }} className="grid animate-fade-in grid-cols-[150px_130px_1fr_130px_130px] items-center gap-4 border-t border-line px-6 py-3 first:border-t-0 hover:bg-mist/60">
                    <span className="text-small tabular-nums text-muted">{dateTime(t.ts)}</span>
                    <span className="flex items-center gap-2 text-small"><IconChip className={`!h-7 !w-7 ${typeChip(t)}`}>{txnIcon(t, 14)}</IconChip>{typeLabel(t)}</span>
                    <span className="flex min-w-0 flex-col"><span className="truncate text-small font-medium">{t.title}</span><span className="truncate text-caption text-muted">{t.detail}</span></span>
                    <Amount t={t} className="text-right text-small" />
                    <span className="text-right text-small tabular-nums text-muted">{money4(t.balance)}</span>
                  </li>
                ))}
              </ul>
            )}
            {rows.length > 0 && <div className="flex items-center justify-end border-t border-line px-6 py-4"><span className="text-small text-muted">{netIsAll ? `Net of all ${count(page.total)} matching` : "Net on this page"} <span className="font-semibold tabular-nums text-ink">{signed(net)}</span>{netIsAll && page.totals ? <span className="ml-3">credits {signed(Number(page.totals.credits))} · debits {signed(-Math.abs(Number(page.totals.debits)))}</span> : null}</span></div>}
          </section>
          {page.total > 0 && <Pagination state={pg} noun="transaction" scrollTo={listRef} />}
        </div>
      )}
    </div>
  );
}
